// Automatic re-fetching: on or off, and which profile — the one recommended for
// the latest forecast's severity (re-chosen after every fetch), or a fixed one.
// The profiles and the recommendation are data in app/safety/rules.py; the
// upcoming times under each come from the server (schedule.info), so nothing
// here re-does the arithmetic.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, Stack, Switch, Typography,
} from "@mui/material";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { SAFETY_API, SEVERITY_LABEL, fmtDateTime, profileLabel } from "./format";

const RECOMMENDED = "";

const fmtHours = (h) => (h >= 48 && h % 24 === 0 ? `${h / 24} days` : `${h} h`);
const fmtEvery = (h) => (h === 24 ? "daily" : `every ${h} h`);

// "Daily from 7 days before; every 12 h from 3 days; every 6 h from 24 h."
export function describeProfile(profile) {
  const stages = [...profile.stages].sort((a, b) => b.from_hours - a.from_hours);
  const text = stages.map((s, i) => `${fmtEvery(s.every_hours)} from ${fmtHours(s.from_hours)}${i === 0 ? " before the start" : ""}`)
    .join("; ");
  return `${text[0].toUpperCase()}${text.slice(1)}.`;
}

export default function ScheduleDialog({ open, assessment: a, config, onClose, onSaved }) {
  const fullScreen = useFullScreenDialog();
  const [auto, setAuto] = useState(false);
  const [profile, setProfile] = useState(RECOMMENDED);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAuto(!!a.auto_refresh);
    setProfile(a.refresh_profile || RECOMMENDED);
  }, [open, a.auto_refresh, a.refresh_profile]);

  const sched = a.schedule || {};
  const severity = sched.severity;
  const chosen = profile || sched.recommended;
  const upcoming = sched.upcoming?.[chosen] || [];

  const save = () => {
    setSaving(true);
    axios.put(`${SAFETY_API}/assessments/${a.id}/schedule`, { auto_refresh: auto, refresh_profile: profile || null })
      .then(res => onSaved(res.data)).catch(() => {}).finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Refresh schedule</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <FormControlLabel control={<Switch checked={auto} onChange={e => setAuto(e.target.checked)} />}
            label="Fetch the forecasts automatically" />
          <Typography variant="body2" color="text.secondary">
            A scheduled fetch is recorded, judged and compared with the one before exactly like a
            manual one. Fetches are counted back from the window's start
            ({fmtDateTime(a.window_start)}) and stop there; nothing is fetched more than 7 days
            ahead, where the Met Office forecast doesn't reach. The result shows here and on the
            list of assessments; to be texted when it changes things, set up SMS alerts.
          </Typography>

          <RadioGroup value={profile} onChange={e => setProfile(e.target.value)}>
            <FormControlLabel value={RECOMMENDED} disabled={!auto} control={<Radio />} label={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Recommended — follows the weather
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Re-chosen after every fetch from how severe the latest forecast is. Now{" "}
                  {profileLabel(config, sched.recommended)},{" "}
                  {severity ? `because the latest forecast is ${SEVERITY_LABEL[severity].toLowerCase()}.`
                            : "because nothing has been judged in range yet."}
                </Typography>
              </Box>} />
            {config.refresh_profiles.map(p => (
              <FormControlLabel key={p.key} value={p.key} disabled={!auto} control={<Radio />} label={
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p.label}
                    {p.key === sched.recommended && (
                      <Typography component="span" variant="caption" color="text.secondary"> (recommended now)</Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{describeProfile(p)}</Typography>
                </Box>} />
            ))}
          </RadioGroup>

          {auto && (
            upcoming.length ? (
              <Alert severity="info">
                Next fetches under {profileLabel(config, chosen)}: {upcoming.slice(0, 4).map(fmtDateTime).join("; ")}
                {upcoming.length > 4 ? "…" : "."}
                {!profile && " A change in the weather can change these."}
              </Alert>
            ) : (
              <Alert severity="info">No fetches left under {profileLabel(config, chosen)} before the start.</Alert>
            )
          )}
          {auto && (
            <Typography variant="caption" color="text.secondary">
              Each point costs two Met Office calls per fetch, and the free tier allows 360 a day across
              every assessment.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
